import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { 
  Users, Calendar, Heart, ArrowRight, 
  Sparkles, Baby, Bell, ChevronRight, UserCheck, MessageCircle
} from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({});

  useEffect(() => {
    fetchDashboardData();
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content?section=dashboard`);
      setContent(response.data);
    } catch (error) {
      console.error("Content fetch error:", error);
    }
  };

  // Helper to get content value with fallback
  const c = (key, fallback) => content[key]?.value || fallback;

  const fetchDashboardData = async () => {
    try {
      const [connectionsRes, requestsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/connections`, { withCredentials: true }),
        axios.get(`${API}/connections/requests`, { withCredentials: true }),
        axios.get(`${API}/events`, { withCredentials: true })
      ]);
      
      setConnections(connectionsRes.data.slice(0, 5));
      setConnectionRequests(requestsRes.data);
      setUpcomingEvents(eventsRes.data.slice(0, 3));
    } catch (error) {
      console.error("Dashboard data error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.put(`${API}/connections/${requestId}/accept`, {}, { withCredentials: true });
      fetchDashboardData();
    } catch (error) {
      console.error("Accept error:", error);
    }
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'ttc': return <Sparkles className="w-4 h-4" />;
      case 'pregnant': return <Heart className="w-4 h-4" />;
      case 'postpartum': return <Baby className="w-4 h-4" />;
      default: return <Heart className="w-4 h-4" />;
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 'ttc': return 'Trying to Conceive';
      case 'pregnant': return 'Pregnant';
      case 'postpartum': return 'Postpartum';
      default: return phase;
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      
      {/* Header */}
      <TopHeader />
      
      <div className="px-6 pt-4 pb-2 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <p className="text-muted-foreground text-sm">{c("dashboard_welcome_text", "Welcome back,")}</p>
          <h1 className="text-2xl font-serif text-sage-darker">{user?.name?.split(' ')[0]}</h1>
        </div>
      </div>

      <main className="px-6 py-6 space-y-6 max-w-7xl mx-auto" data-testid="dashboard-main">
        {/* Connection Requests - Prominent Alert */}
        {connectionRequests.length > 0 && (
          <Card className="bg-gradient-to-r from-sage/30 to-sage/10 border-sage/30 shadow-md" data-testid="connection-requests">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-sage rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sage-darker font-semibold">New Connection Requests</p>
                  <p className="text-sm text-sage-dark">{connectionRequests.length} people want to connect with you</p>
                </div>
              </div>
              <div className="space-y-2">
                {connectionRequests.slice(0, 3).map((request) => (
                  <div key={request.request_id} className="flex items-center gap-3 p-3 rounded-xl bg-white">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={request.from_user?.picture} />
                      <AvatarFallback className="bg-sage/20 text-sage-dark">
                        {request.from_user?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sage-darker font-medium truncate">{request.from_user?.name}</p>
                      <Badge variant="outline" className={`text-xs phase-${request.from_user?.phase}`}>
                        {getPhaseLabel(request.from_user?.phase)}
                      </Badge>
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-sage text-white hover:bg-sage-dark"
                      onClick={() => handleAcceptRequest(request.request_id)}
                      data-testid={`accept-request-${request.request_id}`}
                    >
                      Accept
                    </Button>
                  </div>
                ))}
              </div>
              {connectionRequests.length > 3 && (
                <Link to="/discover" className="block mt-3">
                  <Button variant="outline" className="w-full border-sage text-sage-dark">
                    View all {connectionRequests.length} requests
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/discover" className="block">
            <Card className="bg-white border-sage/20 hover:shadow-md transition-shadow" data-testid="discover-card">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-sage/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-sage-dark" strokeWidth={1.5} />
                </div>
                <p className="text-sage-darker font-medium">{c("dashboard_discover_title", "Discover")}</p>
                <p className="text-xs text-muted-foreground">{c("dashboard_discover_subtitle", "Find your tribe")}</p>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/circles" className="block">
            <Card className="bg-white border-sage/20 hover:shadow-md transition-shadow" data-testid="circles-card">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-clay/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Heart className="w-6 h-6 text-clay-dark" strokeWidth={1.5} />
                </div>
                <p className="text-sage-darker font-medium">{c("dashboard_circles_title", "Circles")}</p>
                <p className="text-xs text-muted-foreground">{c("dashboard_circles_subtitle", "Join groups")}</p>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/experts" className="block">
            <Card className="bg-white border-sage/20 hover:shadow-md transition-shadow" data-testid="experts-card">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <UserCheck className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                </div>
                <p className="text-sage-darker font-medium">{c("dashboard_experts_title", "Experts")}</p>
                <p className="text-xs text-muted-foreground">{c("dashboard_experts_subtitle", "1:1 Sessions")}</p>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/events" className="block">
            <Card className="bg-white border-sage/20 hover:shadow-md transition-shadow" data-testid="events-card">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Calendar className="w-6 h-6 text-purple-600" strokeWidth={1.5} />
                </div>
                <p className="text-sage-darker font-medium">{c("dashboard_events_title", "Events")}</p>
                <p className="text-xs text-muted-foreground">{c("dashboard_events_subtitle", "Join sessions")}</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Your Connections - Clickable to Chat */}
        <Card className="bg-white border-sage/20" data-testid="connections-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-sage" />
                Your Connections
              </CardTitle>
              <Link to="/discover" className="text-sage-dark text-sm flex items-center gap-1 hover:underline">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {connections.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-sage/40 mx-auto mb-3" />
                <p className="text-muted-foreground">{c("dashboard_no_connections_text", "No connections yet")}</p>
                <Link to="/discover">
                  <Button className="mt-4 bg-sage text-white hover:bg-sage-dark rounded-full" data-testid="find-people-btn">
                    {c("dashboard_find_tribe_btn", "Find Your Tribe")}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {connections.map((conn) => (
                  <div 
                    key={conn.request_id} 
                    className="flex items-center gap-3 p-3 rounded-xl bg-cream hover:bg-sage/10 cursor-pointer transition-colors"
                    onClick={() => navigate(`/chat/${conn.request_id}`)}
                    data-testid={`connection-${conn.connected_user?.user_id}`}
                  >
                    <Avatar className="h-12 w-12 border-2 border-sage/30">
                      <AvatarImage src={conn.connected_user?.picture} />
                      <AvatarFallback className="bg-sage/20 text-sage-dark">
                        {conn.connected_user?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sage-darker font-medium truncate">{conn.connected_user?.name}</p>
                      <p className="text-xs text-muted-foreground">Tap to chat</p>
                    </div>
                    <MessageCircle className="w-5 h-5 text-sage" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="bg-white border-sage/20" data-testid="upcoming-events">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif text-sage-darker">Upcoming Events</CardTitle>
              <Link to="/events" className="text-sage-dark text-sm flex items-center gap-1 hover:underline">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {upcomingEvents.length === 0 ? (
              <div className="text-center py-6">
                <Calendar className="w-10 h-10 text-sage/40 mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">No upcoming events</p>
              </div>
            ) : (
              upcomingEvents.map((event) => (
                <div 
                  key={event.event_id} 
                  className="flex items-center gap-4 p-3 rounded-xl bg-cream hover:bg-cream-dark transition-colors cursor-pointer"
                  onClick={() => navigate(`/events?selected=${event.event_id}`)}
                  data-testid={`event-${event.event_id}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-sage/20 flex flex-col items-center justify-center">
                    <p className="text-sage-dark font-bold text-lg">
                      {new Date(event.scheduled_at).getDate()}
                    </p>
                    <p className="text-sage/70 text-xs uppercase">
                      {new Date(event.scheduled_at).toLocaleDateString('en', { month: 'short' })}
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sage-darker font-medium truncate">{event.title}</p>
                    <p className="text-muted-foreground text-sm">{event.host_name}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-sage/10 text-sage-dark border-0">
                      {new Date(event.scheduled_at).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                    </Badge>
                    {event.is_registered && (
                      <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                        ✓ Registered
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
